<template>
  <div class="tasks-card" :class="{ minified: minified }">
    <div class="tasks-card__header">
      <h1 class="tasks-card__title" v-if="!minified">{{ $t('opportunity-tasks.title') }}</h1>
      <div class="tasks-card__header-actions">
        <BaseButton
          has-icon-left
          is-outlined
          :is-small="minified"
          is-square
          @click.native="onImport"
          :tooltip-text="$t('opportunity-tasks.import-tasks-tooltip')"
        >
          <BaseIcon name="plus" />
          {{ $t('opportunity-tasks.import-tasks') }}
        </BaseButton>
        <BaseButton has-icon-left is-square :is-small="minified" @click.native="$emit('task-create')">
          <BaseIcon name="plus" />
          {{ $t('opportunity-tasks.create-task') }}
        </BaseButton>
      </div>
    </div>
    <div class="tasks-card__extraction-progress">
      <FieldsExtractionProgress />
      <RequirementsExtractionProgress />
    </div>

    <!-- Empty state -->
    <div v-if="tasks.length === 0 && !(isFieldsExtracting || isRequirementsExtracting)" class="tasks-card__empty">
      <div class="tasks-card__empty-icon">
        <BaseIcon name="clipboard" />
      </div>
      <div class="tasks-card__empty-body">
        <span class="tasks-card__empty-title">{{ $t('opportunity-tasks.empty.title') }}</span>
        <span class="tasks-card__empty-subtitle">{{ $t('opportunity-tasks.empty.subtitle') }}</span>
      </div>
      <BaseButton has-icon-right is-square @click.native="$emit('task-create')">
        {{ $t('opportunity-tasks.create-task') }}
        <BaseIcon name="plus" />
      </BaseButton>
    </div>

    <div v-else class="tasks-card__content">
      <div
        v-for="task in tasks"
        :key="task.id"
        class="tasks-card__item"
        role="button"
        tabindex="0"
        @click.stop="() => onTaskClick(task)"
      >
        <div class="tasks-card__item-left">
          <div :class="`${getAssignees(task).length > 0 ? 'tasks-card__assignees' : ''} assignees-${task.id}`">
            <BaseAssignation
              :value="getAssignees(task)"
              @change="(userIds) => onAssign(task, userIds)"
              :attach="`.assignees-${task.id}`"
              tooltipPlacement="bottom"
            />
          </div>
          <div class="tasks-card__item-body">
            <span class="tasks-card__item-title">{{ task.title }}</span>
            <span
              class="tasks-card__item-meta"
              v-tooltip="{
                content: $t('opportunity-tasks.task-details.from-template'),
                placement: 'top',
                delay: 500,
              }"
              v-if="task.taskTemplateId"
            >
              <BaseIcon v-if="task.taskTemplateId" name="lock" class="tasks-card__lock-icon" />
            </span>
          </div>
        </div>
        <div :class="`task-dialog__status-${task.id}`">
          <BaseStatusMenu
            ref="taskStatusMenu"
            :items="taskStatuses"
            :current-status="task.taskStatus"
            :attach="`.task-dialog__status-${task.id}`"
            tooltipPlacement="left"
            is-disabled
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon';
import { mapGetters } from 'vuex';
import BaseAssignation from '@/components/base/BaseAssignation.vue';
import BaseStatusMenu from '@/components/base/BaseStatusMenu.vue';
import FieldsExtractionProgress from '@/components/page-opportunity-details/FieldsExtractionProgress.vue';
import RequirementsExtractionProgress from '@/components/page-opportunity-tasks/RequirementsExtractionProgress.vue';

export default {
  name: 'TasksCard',
  components: {
    FieldsExtractionProgress,
    RequirementsExtractionProgress,
    BaseStatusMenu,
    BaseAssignation,
    BaseButton,
    BaseIcon,
  },
  props: {
    tasks: {
      type: Array,
      default: () => [],
    },
    minified: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('taskStatuses', ['taskStatuses']),
    ...mapGetters('fieldsExtraction', { isFieldsExtracting: 'isExtracting' }),
    ...mapGetters('requirementsExtraction', ['isRequirementsExtracting']),
  },
  methods: {
    onImport() {
      this.$emit('tasks-import');
    },
    isCurrentUser(assignee) {
      return assignee.userId === this.currentUser.id;
    },
    getAssignees(task) {
      return task.assignations.map((a) => ({ ...a, userId: a.id }));
    },
    onAssign(task, userIds) {
      this.$emit('task-assign', { userIds, task });
    },
    onTaskClick(task) {
      this.$emit('task-click', task);
    },
  },
};
</script>

<style lang="scss">
.tasks-card {
  background-color: $color-white;
  border-radius: $border-radius-root;
  padding: 24px 0;
  position: relative;
  box-shadow: $shadow-default;

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 24px;
    margin-bottom: 20px;
    padding: 0 32px 16px 32px;
    border-bottom: 1px solid $color-border;
  }

  &__title {
    font-size: $font-size-xbigger;
    font-weight: 900;
    color: $color-normal;
    display: flex;
    align-items: center;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
    width: 100%;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 0;
    overflow-y: auto;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    margin: 0 20px;
    border-bottom: 1px solid $color-border;
    cursor: pointer;
    transition: background-color 200ms ease;
    border-radius: $border-radius-small;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: $color-neutral-pale;
    }
  }

  &__item-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1;
    margin-right: 12px;
  }

  &__assignees {
    padding-right: 10px;
  }

  &__item-body {
    display: flex;
    gap: 8px;
    min-width: 0;
  }

  &__item-title {
    font-size: $font-size-small;
    font-weight: 500;
    color: $color-normal;
    font-family: $font-family-raleway;
  }

  &__item-meta {
    font-size: $font-size-xsmall;
    color: $color-normal-pale;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__lock-icon {
    width: 16px;
    height: 16px;
    color: $color-normal;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 36px;
    gap: 24px;

    &-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 56px;
      height: 56px;
      border-radius: $border-radius-round;
      background-color: $color-neutral-pale;

      .base-icon {
        width: 37px;
        height: 37px;
        color: $color-bluey-gray;
      }
    }

    &-body {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &-title {
      font-size: $font-size-root;
      font-weight: 600;
      color: $color-normal;
      margin-bottom: 8px;
    }

    &-subtitle {
      font-size: $font-size-small;
    }
  }

  &__extraction-progress {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.minified {
    .tasks-card {
      &__header {
        padding: 0 0 8px 0;
        margin-bottom: 16px;
      }

      &__item {
        padding: 8px 8px;
        margin: 0;
      }
    }
  }

  // Phones: the header's buttons under its title; each task's title across the row, then who
  // it's assigned to and its status on the line under it.
  @include screen-below('tablet') {
    &__header {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
      padding-right: $phone-page-gutter;
      padding-left: $phone-page-gutter;
    }

    &__header-actions {
      justify-content: flex-start;
    }

    &__item {
      flex-wrap: wrap;
      gap: 8px;
      margin: 0 8px;
    }

    &__item-left {
      display: contents;
    }

    &__item-body {
      flex-basis: 100%;
      order: -1;
    }

    &__item-title {
      font-size: 15px;
    }

    &__assignees {
      padding-right: 0;
    }

    &__item > :last-child {
      margin-left: auto;
    }
  }
}
</style>
